import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AdoService } from '../services/ado.service';
import { GitHubApiService } from '../services/github-api.service';
import { GitHubAuthService } from '../services/github-auth.service';
import { EnvironmentService } from '../services/environment.service';
import { LoadingService } from '../services/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../dialog/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from '../dialog/success-dialog/success-dialog.component';
import { switchMap, filter, take, catchError, takeUntil } from 'rxjs/operators';
import { PlatformPortalApiService } from '../services/platform-portal-api.service';
import { GitHubDeleteBranchRequest, RepositoryProvider } from '../models/github-delete-branch-request';
import { GitHubBranchOperationResult } from '../models/github-branch-operation-result';
import { Subject } from 'rxjs';
import { WindowService } from '../services/window.service';

@Component({
    selector: 'app-delete-branch-pr',
    templateUrl: './delete-branch-pr.component.html',
    styleUrls: ['./delete-branch-pr.component.css'],
    standalone: false
})
export class DeleteBranchPrComponent implements OnInit, OnDestroy {
  deleteBranchForm: FormGroup;
  branches: string[] = [];
  repositories: any[] = [];
  defaultOrg?: any;
  defaultProject?: string;
  submitted = false;
  protected readonly PROTECTED_BRANCHES = ['master', 'develop', 'main'];
  filteredRepositories: any[] = [];
  filteredBranches: string[] = [];
  isAutoMode: boolean = false;
  gitHubOrg: string | null = null;
  submitButtonText: string = 'Create Pull Request';
  private adoRepositoriesCache: any[] = [];
  private githubRepositoriesCache: any[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly adoService: AdoService,
    private readonly githubApiService: GitHubApiService,
    private readonly githubAuthService: GitHubAuthService,
    private readonly environmentService: EnvironmentService,
    private readonly loadingService: LoadingService,
    private readonly dialog: MatDialog,
    private readonly platformPortalApiService: PlatformPortalApiService,
    private readonly windowService: WindowService
  ) {
    this.deleteBranchForm = this.fb.group({
      repositoryProvider: ['AzureDevOps', Validators.required],
      branchToDelete: ['', [Validators.required, this.protectedBranchValidator()]],
      repository: ['', Validators.required],
    });

    const environment = this.environmentService.getEnvironment();
    this.defaultOrg = { accountName: environment.defaultOrg };
    this.defaultProject = environment.defaultProject;

    this.deleteBranchForm.get('repository')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (!this.isAutoMode) {
        this.filterRepositories(value);
      }
    });

    this.deleteBranchForm.get('branchToDelete')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (!this.isAutoMode) {
        this.filterBranches(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protectedBranchValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const branch = control.value;
      if (this.PROTECTED_BRANCHES.includes(branch)) {
        return { protectedBranch: true };
      }
      return null;
    };
  }

  ngOnInit() {
    this.isAutoMode = this.route.snapshot.queryParams['auto'] === 'true';

    // Subscribe to organizations from GitHubApiService
    this.githubApiService.organizations$.subscribe((orgs) => {
      if (orgs.length > 0) {
        this.gitHubOrg = orgs[0].login;
        console.log('[DeleteBranchPr] GitHub org set to:', this.gitHubOrg);
      }
    });

    this.deleteBranchForm.get('repository')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(repo => {
      if (!this.isAutoMode && repo) {
        this.getBranches(repo);
      }
    });

    // Add listener for repository provider changes
    this.deleteBranchForm.get('repositoryProvider')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((provider: RepositoryProvider) => {
      if (!this.isAutoMode) {
        // Clear repository and branch fields when provider changes
        this.deleteBranchForm.patchValue({
          repository: '',
          branchToDelete: ''
        });

        // Clear arrays
        this.repositories = [];
        this.branches = [];
        this.filteredRepositories = [];
        this.filteredBranches = [];

        // Load repositories for the selected provider
        this.getRepositories();
        this.submitButtonText = provider === 'GitHub' ? 'Delete Branch' : 'Create Pull Request';
      }
    });

    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params['branches']) {
        const queryBranches = Array.isArray(params['branches'])
          ? params['branches']
          : params['branches'].split(',');

        this.branches = [
          ...queryBranches,
          ...this.branches.filter(b => !queryBranches.includes(b))
        ];
      }

      if (params['branchToDelete']) {
        this.deleteBranchForm.patchValue({
          branchToDelete: params['branchToDelete']
        });
      }

      if (params['repo']) {
        this.deleteBranchForm.patchValue({
          repository: params['repo']
        });
      }

      if (params['repoProvider']) {
        this.deleteBranchForm.patchValue({
          repositoryProvider: params['repoProvider']
        });
      }

      this.getRepositories();
    });
  }

  private filterRepositories(value: string) {
    const filterValue = value?.toLowerCase() || '';
    this.filteredRepositories = this.repositories.filter(repo =>
      repo.name.toLowerCase().includes(filterValue)
    );
  }

  private filterBranches(value: string) {
    const filterValue = value?.toLowerCase() || '';
    this.filteredBranches = this.branches.filter(branch =>
      branch.toLowerCase().includes(filterValue)
    );
  }

  getRepositories() {
    const provider = this.deleteBranchForm.get('repositoryProvider')?.value as RepositoryProvider;
    if (provider === "AzureDevOps") {
      this.getAdoRepositories();
    } else if (provider === "GitHub") {
      this.getGitHubRepositories();
    }
  }

  getBranches(repoName: string) {
    const provider = this.deleteBranchForm.get('repositoryProvider')?.value as RepositoryProvider;
    if (provider === "AzureDevOps") {
      this.getAdoBranches(repoName);
    } else if (provider === "GitHub") {
      this.getGitHubBranches(repoName);
    }
  }

  getAdoRepositories() {
    if (this.adoRepositoriesCache.length > 0) {
      this.repositories = [...this.adoRepositoriesCache];
      this.filteredRepositories = [...this.adoRepositoriesCache];
      if (this.isAutoMode) {
        this.onSubmit();
      }
      return;
    }

    this.adoService.GetRepositories(this.defaultOrg.accountName, this.defaultProject!)
      .subscribe(repos => {
        const sortedRepos = repos.value.sort((a: any, b: any) => {
          if (a.name === this.route.snapshot.queryParams['repo']) return -1;
          if (b.name === this.route.snapshot.queryParams['repo']) return 1;
          return a.name.localeCompare(b.name);
        });
        this.repositories = [...sortedRepos];
        this.filteredRepositories = [...sortedRepos];
        this.adoRepositoriesCache = [...sortedRepos];
        if (this.isAutoMode) {
          this.onSubmit();
        }
      });
  }

  getAdoBranches(repoName: string) {
    const repo = this.repositories.find(r => r.name === repoName);
    if (repo) {
      this.adoService.GetBranches(this.defaultOrg.accountName, this.defaultProject!, repo.id)
        .subscribe(branches => {
          const queryBranches = this.route.snapshot.queryParams['branches']?.split(',') || [];

          this.branches = branches.value
            .map((b: any) => b.name.replace('refs/heads/', ''))
            .sort((a: string, b: string) => {
              if (queryBranches.includes(a)) return -1;
              if (queryBranches.includes(b)) return 1;
              return a.localeCompare(b);
            });
          this.filteredBranches = [...this.branches];
        });
    }
  }

  getGitHubRepositories() {
    if (this.githubRepositoriesCache.length > 0) {
      this.repositories = [...this.githubRepositoriesCache];
      this.filteredRepositories = [...this.githubRepositoriesCache];
      if (this.isAutoMode) {
        this.onSubmit();
      }
      return;
    }

    if (!this.gitHubOrg) {
      console.warn('GitHub organization not available');
      return;
    }

    this.githubApiService.getOrganizationRepositories(this.gitHubOrg).subscribe(repos => {
      const sortedRepos = repos.sort((a: any, b: any) => {
        if (a.name === this.route.snapshot.queryParams['repo']) return -1;
        if (b.name === this.route.snapshot.queryParams['repo']) return 1;
        return a.name.localeCompare(b.name);
      });
      this.repositories = [...sortedRepos];
      this.filteredRepositories = [...sortedRepos];
      this.githubRepositoriesCache = [...sortedRepos];
      if (this.isAutoMode) {
        this.onSubmit();
      }
    });
  }

  getGitHubBranches(repoName: string) {
    if (!this.gitHubOrg) {
      console.warn('GitHub organization not available');
      return;
    }

    this.githubApiService.getBranches(this.gitHubOrg, repoName).subscribe((branches: any[]) => {
      const queryBranches = this.route.snapshot.queryParams['branches']?.split(',') || [];

      this.branches = branches
        .map((b: any) => b.name)
        .sort((a: string, b: string) => {
          if (queryBranches.includes(a)) return -1;
          if (queryBranches.includes(b)) return 1;
          return a.localeCompare(b);
        });
      this.filteredBranches = [...this.branches];
    });
  }

  onSubmit() {
    this.submitted = true;
    const branchToDelete = this.deleteBranchForm.get('branchToDelete')?.value;
    const repository = this.deleteBranchForm.get('repository')?.value;
    const isAutoMode = this.route.snapshot.queryParams['auto'] === 'true';

    if (!isAutoMode) {
        if (!this.repositories.some(repo => repo.name === repository)) {
            this.dialog.open(ErrorDialogComponent, {
                data: {
                    title: 'Invalid Repository',
                    message: `The repository "${repository}" does not exist.`
                }
            });
            return;
        }

        if (!this.branches.includes(branchToDelete)) {
            this.dialog.open(ErrorDialogComponent, {
                data: {
                    title: 'Invalid Branch',
                    message: `The branch "${branchToDelete}" does not exist in repository "${repository}".`
                }
            });
            return;
        }
    }

    if (this.deleteBranchForm.valid) {
        const repositoryProvider = this.deleteBranchForm.get('repositoryProvider')?.value as RepositoryProvider;

        if (repositoryProvider === 'GitHub') {
            const request: GitHubDeleteBranchRequest = {
                repositoryName: repository,
                branchName: branchToDelete,
                provider: repositoryProvider
            };
            this.deleteGitHubBranch(request);
        } else if (repositoryProvider === 'AzureDevOps') {
            this.createDeleteBranchPR(repository, branchToDelete);
        }
    } else if (this.PROTECTED_BRANCHES.includes(branchToDelete)) {
        this.dialog.open(ErrorDialogComponent, {
            data: {
                title: 'Protected Branch',
                message: `The branch "${branchToDelete}" cannot be deleted via the platform portal as it is a protected branch.`
            }
        });
        return;
    }
  }

  deleteGitHubBranch(request: GitHubDeleteBranchRequest): void {
    this.loadingService.show('delete-branch-pr');

    this.platformPortalApiService.deleteGitHubBranch(request)
      .subscribe({
        next: (result) => this.handleDeleteResponse(result),
        error: (error) => this.handleDeleteError(error)
      });
  }

  private createDeleteBranchPR(repository: string, branchToDelete: string) {
    const formattedRepo = repository.toLowerCase().replace(/\s+/g, '-');
    const prBranchName = `delete-${branchToDelete}-from-${formattedRepo}`;
    const environment = this.environmentService.getEnvironment();
    const githubOwner = environment.github.org;
    const githubRepo = 'delete-branch';

    // Check if GitHub is connected before proceeding
    if (!this.githubAuthService.isConnected()) {
      this.dialog.open(ErrorDialogComponent, {
        data: {
          title: 'GitHub Not Connected',
          message: 'Please connect to GitHub first before creating a delete branch request.'
        }
      });
      return;
    }

    // Start loading indicator
    this.loadingService.show('delete-branch-pr');

    // Wait for GitHub auth service to be ready
    this.githubAuthService.connectionInfo$.pipe(
      filter(info => info.isConnected && !info.error),
      take(1),
      switchMap(() => this.githubApiService.getRepository(githubOwner, githubRepo))
    ).subscribe({
      next: (repoInfo) => {
        const defaultBranch = repoInfo.default_branch || 'main';
        // Repository exists, proceed with creating branch and PR
        this.createBranchAndPR(repository, branchToDelete, prBranchName, githubOwner, githubRepo, defaultBranch)
          .subscribe({
            next: this.handlePRSuccess.bind(this),
            error: this.handlePRError.bind(this)
          });
      },
      error: (error) => this.handlePRError(error)
    });
  }

  private createBranchAndPR(repository: string, branchToDelete: string, prBranchName: string, githubOwner: string, githubRepo: string, defaultBranch: string) {
    return this.githubApiService.getBranch(githubOwner, githubRepo, defaultBranch)
      .pipe(
        switchMap(masterBranch => this.createNewBranch(masterBranch, prBranchName, githubOwner, githubRepo)),
        switchMap(() => this.updateRequestJson(repository, branchToDelete, prBranchName, githubOwner, githubRepo)),
        switchMap(() => this.createPR(branchToDelete, prBranchName, githubOwner, githubRepo, defaultBranch))
      );
  }

  private createNewBranch(masterBranch: any, prBranchName: string, githubOwner: string, githubRepo: string) {
    return this.githubApiService.createBranch(
      githubOwner,
      githubRepo,
      prBranchName,
      masterBranch.commit.sha
    );
  }

  private updateRequestJson(repository: string, branchToDelete: string, prBranchName: string, githubOwner: string, githubRepo: string) {
    const fileContent = {
      repoName: repository,
      branchName: branchToDelete
    };

    // First get the current file content to obtain its SHA
    return this.githubApiService.getRepositoryContent(githubOwner, githubRepo, 'request.json').pipe(
      switchMap(fileData => {
        // Use the SHA from the existing file
        return this.githubApiService.updateFileContent({
          owner: githubOwner,
          repo: githubRepo,
          path: 'request.json',
          message: 'Update request.json',
          content: JSON.stringify(fileContent, null, 2),
          branch: prBranchName,
          sha: fileData.sha
        });
      }),
      catchError(error => {
        // If file doesn't exist (404), create it without SHA
        if (error.status === 404) {
          return this.githubApiService.updateFileContent({
            owner: githubOwner,
            repo: githubRepo,
            path: 'request.json',
            message: 'Create request.json',
            content: JSON.stringify(fileContent, null, 2),
            branch: prBranchName
          });
        }
        // Re-throw other errors
        throw error;
      })
    );
  }

  private createPR(branchToDelete: string, prBranchName: string, githubOwner: string, githubRepo: string, defaultBranch: string) {
    return this.githubApiService.createPullRequest({
      owner: githubOwner,
      repo: githubRepo,
      title: `Delete branch ${branchToDelete}`,
      head: prBranchName,
      base: defaultBranch,
      body: 'This PR was automatically generated to delete a branch',
      draft: false
    });
  }

  private handlePRSuccess(response: any) {
    // Hide loading indicator
    this.loadingService.hide('delete-branch-pr');

    if (response?.number) {
      const environment = this.environmentService.getEnvironment();
      const githubOwner = environment.github.org;
      const githubUrl = `https://github.com/${githubOwner}/delete-branch/pull/${response.number}`;
      const isAutoSubmit = this.route.snapshot.queryParams['auto'] === 'true';

      if (isAutoSubmit) {
        this.windowService.setLocationHref(githubUrl);
      } else {
        this.dialog.open(SuccessDialogComponent, {
          data: {
            message: `Pull request created successfully. PR #${response.number}`,
            link: githubUrl,
            linkText: 'View Pull Request'
          }
        });
      }
    }
  }

  private handlePRError(error: any) {
    // Hide loading indicator
    this.loadingService.hide('delete-branch-pr');

    let errorMessage = '';
    let errorTitle = 'Error Creating Pull Request';
    let link = '';
    let linkText = '';

    if (error.branchExists && error.branchUrl) {
      errorTitle = 'Branch Already Exists';
      errorMessage = `A branch with this name already exists. You can view the existing branch to see if it contains the changes you need.`;
      link = error.branchUrl;
      linkText = 'View Existing Branch';
    } else if (error.message?.includes('GitHub not connected')) {
      errorTitle = 'GitHub Not Connected';
      errorMessage = 'Please connect to GitHub first before creating a delete branch request.';
    } else if (error.status === 404) {
      const environment = this.environmentService.getEnvironment();
      errorMessage = `Repository not found. Please verify that the '${environment.github.org}/delete-branch' repository exists and you have access to it.`;
    } else if (error.status === 409) {
      errorMessage = 'A pull request already exists for this branch.';
    } else {
      errorMessage = 'An unexpected error occurred while creating the pull request.';
    }

    this.dialog.open(ErrorDialogComponent, {
      data: {
        title: errorTitle,
        message: link ? errorMessage : `${errorMessage} Response: ${error.message || error}`,
        link: link,
        linkText: linkText
      }
    });
  }

  private handleDeleteResponse(result: GitHubBranchOperationResult) {
    this.loadingService.hide('delete-branch-pr');
    if (!result.success) {
      const errorDetails = result.errors.length > 0 ? `\n\nDetails:\n${result.errors.join('\n')}` : '';
      this.dialog.open(ErrorDialogComponent, {
        data: {
          message: `${result.message}${errorDetails}`,
        }
      });
    }
    else {
      this.dialog.open(SuccessDialogComponent, {
        data: {
          message: result.message,
        }
      }).afterClosed().subscribe(() => {
        this.reload();
      });
    }
  }

  reload(): void {
    if (this.isAutoMode) {
      this.windowService.setLocationHref('/delete-branch-pr');
    } else {
      this.windowService.nativeWindow.location.reload();
    }
  }

  private handleDeleteError(error: any) {
    this.loadingService.hide('delete-branch-pr');
    const errorResult = error?.error;
    let errorMessage = errorResult?.message || 'An unexpected error occurred while deleting the branch.';
    let errorTitle = 'Error Deleting Branch';

    const errorDetails = errorResult?.errors?.join('\n') || 'No additional details.';

    this.dialog.open(ErrorDialogComponent, {
      data: {
        title: errorTitle,
        message: `${errorMessage}\n\nDetails:\n${errorDetails}`
      }
    });
  }

  hasError(controlName: string): boolean {
    const control = this.deleteBranchForm.get(controlName);
    return !!control?.errors && (control?.dirty || control?.touched || this.submitted);
  }

  getErrorMessage(controlName: string): string {
    const control = this.deleteBranchForm.get(controlName);
    if (control?.errors?.['protectedBranch']) {
      return 'This branch cannot be deleted via the platform portal';
    }
    return 'Please enter or select a branch';
  }
}